import React, { useMemo, useState } from "react";
import { Box, Check, MapPin, Search, Sparkles, Trash2, Users, X } from "lucide-react";
import LazyImage from "./LazyImage";
import { SURFACE, REF_LIMIT } from "./constants";
import { assetMatchesQuery, getAssetImageUrl, getAssetKey } from "./utils";
import useDelayedUnmount from "../../../hooks/useDelayedUnmount";

const TAB_META = [
  { key: "characters", label: "人物", Icon: Users },
  { key: "scenes", label: "场景", Icon: MapPin },
  { key: "props", label: "道具", Icon: Box },
];

/**
 * 参考图选择弹窗
 *
 * 特点：
 *  - 三 Tab 切换，只渲染当前 Tab 的资产
 *  - LazyImage 视口内懒加载缩略图
 *  - 搜索 + 只看已选 + 按剧本匹配 + 一键清空
 *
 * Props:
 *  - open          弹窗是否打开
 *  - onClose       关闭
 *  - assets        { characters: [], scenes: [], props: [] }
 *  - selectedKeys  Set<string>
 *  - limit         默认 REF_LIMIT
 *  - disableToggle 是否整体禁用（生成中）
 *  - onToggle(type, asset) 单张切换
 *  - onClear                清空已选
 *  - onMatchFromScript      按剧本匹配
 */
const AssetPickerModal = ({
  open,
  onClose,
  assets,
  selectedKeys,
  limit = REF_LIMIT,
  disableToggle = false,
  onToggle,
  onClear,
  onMatchFromScript,
  additionalControls,
}) => {
  const [activeTab, setActiveTab] = useState("characters");
  const [query, setQuery] = useState("");
  const [selectedOnly, setSelectedOnly] = useState(false);
  const visibility = useDelayedUnmount(Boolean(open), 140);

  const currentList = assets?.[activeTab] || [];

  const tabStats = useMemo(() => {
    const stats = {};
    TAB_META.forEach(({ key }) => {
      const list = assets?.[key] || [];
      const selectedCount = list.filter((a) => selectedKeys?.has(getAssetKey(key, a))).length;
      stats[key] = { total: list.length, selected: selectedCount };
    });
    return stats;
  }, [assets, selectedKeys]);

  const visibleList = useMemo(() => {
    return currentList
      .filter((asset) => !selectedOnly || selectedKeys?.has(getAssetKey(activeTab, asset)))
      .filter((asset) => assetMatchesQuery(asset, query))
      .slice()
      .sort((a, b) => {
        const aSel = selectedKeys?.has(getAssetKey(activeTab, a)) ? 1 : 0;
        const bSel = selectedKeys?.has(getAssetKey(activeTab, b)) ? 1 : 0;
        if (aSel !== bSel) return bSel - aSel;
        return String(a?.name || "").localeCompare(String(b?.name || ""), "zh-Hans-CN");
      });
  }, [activeTab, currentList, query, selectedKeys, selectedOnly]);

  if (!visibility.mounted) return null;

  const totalSelected = Object.values(tabStats).reduce((sum, s) => sum + s.selected, 0);
  const reachLimit = totalSelected >= limit;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${visibility.exiting ? "ui-backdrop-exit" : "ui-backdrop-enter"}`}
      style={{ backgroundColor: "var(--overlay-heavy)" }}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-6xl max-h-[92vh] rounded-lg border flex flex-col overflow-hidden ${visibility.exiting ? "ui-modal-exit" : "ui-modal-enter"}`}
        style={{ backgroundColor: SURFACE.panel, borderColor: "var(--color-border-primary)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="shrink-0 flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}
        >
          <div>
            <div className="text-base font-bold" style={{ color: "var(--color-text-primary)" }}>参考图库</div>
            <div className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>
              已选 {totalSelected} / {limit} · 选中本集用到的人物、场景、道具参考图
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ui-pressable h-9 w-9 rounded-md border inline-flex items-center justify-center cursor-pointer"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-muted)" }}
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tab + 工具栏 */}
        {additionalControls && <div className="shrink-0 px-4 py-3">{additionalControls}</div>}
        <div
          className="shrink-0 px-4 py-3 border-b space-y-3"
          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}
        >
          <div className="flex items-center gap-2">
            {TAB_META.map(({ key, label, Icon }) => {
              const stat = tabStats[key];
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className="h-9 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer transition-colors"
                  style={{
                    borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-primary)",
                    backgroundColor: active ? "var(--color-accent-light)" : SURFACE.card,
                    color: active ? "var(--color-accent-primary)" : "var(--color-text-secondary)",
                  }}
                >
                  <Icon size={13} />
                  {label}
                  <span
                    className="text-[10px] font-mono px-1 rounded-full"
                    style={{
                      backgroundColor: active ? "var(--color-bg-overlay)" : SURFACE.soft,
                      color: active ? "var(--color-accent-primary)" : "var(--color-text-muted)",
                    }}
                  >
                    {stat.selected}/{stat.total}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="flex-1 min-w-[240px] flex items-center gap-2 h-9 rounded-md border px-2.5"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input }}
            >
              <Search size={14} style={{ color: "var(--color-text-muted)" }} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="按名称 / 别名 / 描述搜索"
                className="flex-1 text-sm bg-transparent outline-none"
                style={{ color: "var(--color-text-primary)" }}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="text-xs cursor-pointer"
                  style={{ color: "var(--color-text-muted)" }}
                >
                  清除
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setSelectedOnly((v) => !v)}
              className="h-9 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
              style={{
                borderColor: selectedOnly ? "var(--color-accent-primary)" : "var(--color-border-primary)",
                backgroundColor: selectedOnly ? "var(--color-accent-light)" : SURFACE.card,
                color: selectedOnly ? "var(--color-accent-primary)" : "var(--color-text-secondary)",
              }}
            >
              <Check size={13} />
              只看已选
            </button>
            {onMatchFromScript && (
              <button
                type="button"
                onClick={onMatchFromScript}
                className="h-9 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
                style={{
                  borderColor: "var(--color-border-primary)",
                  backgroundColor: SURFACE.card,
                  color: "var(--color-text-secondary)",
                }}
              >
                <Sparkles size={13} />
                按剧本匹配
              </button>
            )}
            {onClear && totalSelected > 0 && (
              <button
                type="button"
                onClick={onClear}
                className="h-9 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
                style={{
                  borderColor: "var(--color-border-primary)",
                  backgroundColor: SURFACE.card,
                  color: "var(--color-error)",
                }}
              >
                <Trash2 size={13} />
                清空已选
              </button>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4" style={{ backgroundColor: SURFACE.page }}>
          {visibleList.length === 0 ? (
            <div
              className="h-[260px] flex items-center justify-center text-sm italic"
              style={{ color: "var(--color-text-muted)" }}
            >
              {query ? "没有匹配的资产" : "这个分类下暂时没有资产"}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {visibleList.map((asset) => {
                const key = getAssetKey(activeTab, asset);
                const selected = selectedKeys?.has(key);
                const imageUrl = getAssetImageUrl(asset);
                const btnDisabled = disableToggle || (!selected && reachLimit) || !imageUrl;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onToggle?.(activeTab, asset)}
                    disabled={btnDisabled && !selected}
                    className="group rounded-md border text-left overflow-hidden cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    style={{
                      borderColor: selected ? "var(--color-accent-primary)" : "var(--color-border-primary)",
                      backgroundColor: selected ? "var(--color-accent-light)" : SURFACE.card,
                    }}
                    title={!imageUrl ? "该资产还没有可用图" : undefined}
                  >
                    <div className="relative">
                      <LazyImage
                        src={imageUrl}
                        alt={asset?.name || "参考图"}
                        fallback={
                          <div
                            className="w-full h-full flex items-center justify-center text-xs"
                            style={{ color: "var(--color-text-muted)" }}
                          >
                            无图
                          </div>
                        }
                      />
                      <span
                        className="absolute top-2 right-2 w-6 h-6 rounded-md border flex items-center justify-center"
                        style={{
                          borderColor: selected ? "var(--color-accent-primary)" : "var(--color-border-primary)",
                          backgroundColor: selected ? "var(--color-accent-primary)" : "color-mix(in srgb, var(--color-bg-card) 88%, transparent)",
                          color: selected ? "#fff" : "var(--color-text-muted)",
                        }}
                      >
                        {selected ? <Check size={13} /> : null}
                      </span>
                    </div>
                    <div className="p-2 space-y-0.5">
                      <div className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
                        {asset?.name || "未命名"}
                      </div>
                      <div
                        className="text-xs leading-5 line-clamp-2"
                        style={{ color: "var(--color-text-muted)" }}
                      >
                        {asset?.description || asset?.aliases || "点击加入本集参考图"}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="shrink-0 px-4 py-3 border-t flex items-center justify-between"
          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}
        >
          <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>
            {reachLimit ? "已到上限，需先取消几张" : `最多可以选 ${limit} 张`}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 rounded-md border text-xs font-bold cursor-pointer"
            style={{
              borderColor: "var(--color-accent-primary)",
              backgroundColor: "var(--color-accent-primary)",
              color: "var(--color-accent-contrast)",
            }}
          >
            完成
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetPickerModal;
